import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { apiService } from '../services/api';
import { socketService } from '../services/socketService';

interface AlertNotificationsContextValue {
  unreadConversationIds: Set<number>;
  setUnreadConversationIds: (ids: Set<number>) => void;
  markConversationRead: (conversationId: number) => void;
  handleConversationViewed: (conversationId: number) => void;
  registerRefreshCallback: (fn: () => void) => void;
}

const AlertNotificationsContext = createContext<AlertNotificationsContextValue>({
  unreadConversationIds: new Set(),
  setUnreadConversationIds: () => {},
  markConversationRead: () => {},
  handleConversationViewed: () => {},
  registerRefreshCallback: () => {},
});

export const AlertNotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) =>
{
  const [unreadConversationIds, setUnreadConversationIds] = useState<Set<number>>(new Set());
  const refreshCallbackRef = useRef<(() => void) | null>(null);

  const handleSetUnread = useCallback((ids: Set<number>) =>
  {
    setUnreadConversationIds(ids);
  }, []);

  const removeFromSet = useCallback((conversationId: number) =>
  {
    setUnreadConversationIds(prev =>
    {
      if (!prev.has(conversationId)) return prev;
      const next = new Set(prev);
      next.delete(conversationId);
      return next;
    });
  }, []);

  const markConversationRead = useCallback(
    (conversationId: number) =>
    {
      removeFromSet(conversationId);
    },
    [removeFromSet],
  );

  const handleConversationViewed = useCallback((conversationId: number) =>
  {
    if (!conversationId) return;
    setUnreadConversationIds(prev =>
    {
      if (!prev.has(conversationId)) return prev;
      // Remove from set and call API - backend will emit socket for sync
      apiService
        .put(`notification/SetViewedByConversationId?conversationId=${conversationId}`)
        .catch(() => {});
      const next = new Set(prev);
      next.delete(conversationId);
      return next;
    });
  }, []);

  const registerRefreshCallback = useCallback((fn: () => void) =>
  {
    refreshCallbackRef.current = fn;
  }, []);

  // Add blue bubble when new notification arrives
  useEffect(() =>
  {
    const unsub = socketService.on('socket:client:notification', (payload: any) =>
    {
      const conversationId: number | undefined =
        payload?.Conversation?.ConversationId ?? payload?.ConversationId;
      if (!conversationId) return;
      setUnreadConversationIds(prev =>
      {
        if (prev.has(conversationId)) return prev;
        const next = new Set(prev);
        next.add(conversationId);
        return next;
      });
    });
    return unsub;
  }, []);

  // Remove blue bubble when conversation marked read, debounce counter refresh
  useEffect(() =>
  {
    let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
    let isRefreshing = false;
    let lastRefreshTime = 0;
    const THROTTLE_MS = 3000;

    const unsub = socketService.on('socket:client:updateViewed', (payload: any) =>
    {
      const conversationId: number | undefined = payload?.conversationId;
      const delay: number = payload?.delay ?? 3;

      // Remove blue bubble immediately
      if (conversationId && conversationId !== 0)
      {
        setUnreadConversationIds(prev =>
        {
          if (!prev.has(conversationId)) return prev;
          const next = new Set(prev);
          next.delete(conversationId);
          return next;
        });
      }

      // Skip if refresh in progress
      if (isRefreshing) return;

      // Throttle: min 3s between refreshes
      const now = Date.now();
      if (lastRefreshTime > 0 && now - lastRefreshTime < THROTTLE_MS) return;

      // Debounce: reset timer on each notification
      if (refreshTimerId !== null) clearTimeout(refreshTimerId);

      // Compress delay (user turn ÷ 10): delay=100 → 10s
      const compressedDelay = delay > 0 ? delay / 10 : 2;
      const totalDelay = compressedDelay * 1000 + Math.random() * 500;

      refreshTimerId = setTimeout(async () =>
      {
        isRefreshing = true;
        lastRefreshTime = Date.now();
        try
        {
          await refreshCallbackRef.current?.();
        }
        finally
        {
          isRefreshing = false;
          refreshTimerId = null;
        }
      }, totalDelay);
    });

    return () =>
    {
      unsub();
      if (refreshTimerId !== null) clearTimeout(refreshTimerId);
    };
  }, []);

  return (
    <AlertNotificationsContext.Provider
      value={{
        unreadConversationIds,
        setUnreadConversationIds: handleSetUnread,
        markConversationRead,
        handleConversationViewed,
        registerRefreshCallback,
      }}
    >
      {children}
    </AlertNotificationsContext.Provider>
  );
};

export const useAlertNotifications = () => useContext(AlertNotificationsContext);

export default AlertNotificationsContext;
